import React from 'react';
import { useSelector } from 'react-redux';
import { Point, Rectangle } from '@printu/geom';
import { getViewportDesignToVirtual } from '@printu/viewport';
import { hitTestMarker } from 'app/core/markers';
import { getMarkerLayer, } from 'app/core/markers/models';
import { ProjectActions } from 'app/stores/project/actions';
import { getActiveTool, getHoveredMarkerId, getMarker, getMarkerIdAtPoint, getSelection, hasSelection, isMarkerSelected, } from 'app/stores/selectors';
import { useAppStore } from 'app/stores/store';
import { InsertArrow } from 'app/views/components/editor/tools/insert/InsertArrow';
import { InsertBlur } from 'app/views/components/editor/tools/insert/InsertBlur';
import { InsertEllipse } from 'app/views/components/editor/tools/insert/InsertEllipse';
import { InsertHighlight } from 'app/views/components/editor/tools/insert/InsertHightlight';
import { InsertLine } from 'app/views/components/editor/tools/insert/InsertLine';
import { InsertPath } from 'app/views/components/editor/tools/insert/InsertPath';
import { InsertPin } from 'app/views/components/editor/tools/insert/InsertPin';
import { InsertPolyline } from 'app/views/components/editor/tools/insert/InsertPolyline';
import { InsertRect } from 'app/views/components/editor/tools/insert/InsertRect';
import { InsertSpot } from 'app/views/components/editor/tools/insert/InsertSpot';
import { InsertText } from 'app/views/components/editor/tools/insert/InsertText';
import { InsertZoom } from 'app/views/components/editor/tools/insert/InsertZoom';
import { Capabilities } from 'app/utils/Capabilities';
import { getElementFrame } from 'app/utils/DOM';
import { addDoubleTapListener } from 'app/utils/events';
import { call, noop } from 'app/utils/function';
import { getTextCloudPath } from 'app/utils/text';
import { InsertImage } from '../components/editor/tools/insert/InsertImage';
// -----------------------
// Drag item controller
// -----------------------
function getDragPosition(mousePosition, dragPayload) {
    const x = dragPayload.initialPosition.x + (mousePosition.x - dragPayload.down.x);
    const y = dragPayload.initialPosition.y + (mousePosition.y - dragPayload.down.y);
    return { x, y };
}
const getMarkerDragController = (markers, dispatch, getState) => {
    var _a;
    // NOTE: at this point we only support single selection
    const marker = markers[0];
    const { alias } = marker;
    if (alias === 'Text') {
        const textMarker = marker;
        if (textMarker.background) {
            if (((_a = textMarker.background) === null || _a === void 0 ? void 0 : _a.pinned) ===
                true) {
                const $onMove = (controller, point) => {
                    const draggedMarker = getMarker(getState(), controller.itemId);
                    const newPosition = getDragPosition(point, controller);
                    const cloudPathStr = getTextCloudPath(Object.assign(Object.assign({}, draggedMarker), newPosition));
                    dispatch(ProjectActions.itemUpdate({
                        id: controller.itemId,
                        patch: Object.assign(Object.assign({}, newPosition), { background: Object.assign(Object.assign({}, draggedMarker.background), { data: cloudPathStr }) }),
                        undoable: controller.initial,
                    }));
                };
                return {
                    onPointerDown: noop,
                    onPointerDrag: $onMove,
                    onPointerUp: noop,
                };
            }
        }
    }
    const $onMove = (payload, point) => {
        dispatch(ProjectActions.itemUpdate({
            id: payload.itemId,
            patch: getDragPosition(point, payload),
            undoable: payload.initial,
        }));
    };
    return {
        onPointerDown: noop,
        onPointerDrag: $onMove,
        onPointerUp: noop,
    };
};
// -----------------------
// Insert item controllers
// -----------------------
const ToolInsertComponent = {
    Arrow: InsertArrow,
    Line: InsertLine,
    Ellipse: InsertEllipse,
    Rect: InsertRect,
    Path: InsertPath,
    Polyline: InsertPolyline,
    Highlight: InsertHighlight,
    Blur: InsertBlur,
    Spot: InsertSpot,
    Zoom: InsertZoom,
    Pin: InsertPin,
    Text: InsertText,
    Image: InsertImage,
};
const getMarkerInsertController = (alias, defaults, overrides) => call(ToolInsertComponent[alias], defaults, overrides);
// -----------------------
// Hook
// -----------------------
export const useItemSelector = () => {
    const [node, setNode] = React.useState(null);
    const store = useAppStore();
    const { dispatch, getState } = store;
    const activeTool = useSelector((state) => getActiveTool(state));
    const pointerControllerRef = React.useRef();
    const disposePointerController = () => {
        var _a;
        call((_a = pointerControllerRef.current) === null || _a === void 0 ? void 0 : _a.disposer);
        pointerControllerRef.current = null;
    };
    // -----------------------
    // Get viewport fixed pointer position
    // -----------------------
    const getDesignPoint = React.useCallback((evt) => {
        const frame = getElementFrame(node);
        const designPoint = new Point(evt.clientX - frame.x, evt.clientY - frame.y);
        const { viewport } = getState();
        const designRect = new Rectangle(viewport.design);
        const designOrigin = designRect.topLeft;
        if (!designRect.contains(designPoint.x, designPoint.y)) {
            return null;
        }
        const point = getViewportDesignToVirtual(viewport, designPoint.subtract(designOrigin));
        return point;
    }, [node, getState]);
    // -----------------------
    // Drag controller lifecycle
    // -----------------------
    const createController = React.useCallback((payload) => {
        disposePointerController();
        const { handler: controller } = payload;
        if (!controller) {
            return;
        }
        const dragPayload = Object.assign(Object.assign({}, payload), { disposer: noop });
        const $onMove = (evt) => {
            getLogger('HandleDrag', 'debug')('[Drag][onMove]');
            const point = getDesignPoint(evt);
            if (point) {
                call(controller.onPointerDrag, dragPayload, point);
            }
            dragPayload.initial = false;
            evt.stopImmediatePropagation();
            evt.stopPropagation();
        };
        const $onDragEnd = (evt) => {
            getLogger('HandleDrag', 'debug')('[Drag][onDragEnd] ');
            const point = getDesignPoint(evt);
            if (point) {
                call(controller.onPointerUp, dragPayload, point);
            }
        };
        const $onEnd = (evt) => {
            getLogger('HandleDrag', 'debug')('[Drag][onEnd] ');
            $onDragEnd(evt);
            call(controller.onEnd, dragPayload);
            disposePointerController();
        };
        const $onPointerUp = (evt) => {
            getLogger('HandleDrag', 'debug')('[Drag][onPointerUp] ');
            $onDragEnd(evt);
            const { disposeOnRelease = true } = controller;
            if (disposeOnRelease) {
                disposePointerController();
            }
        };
        const $onContext = Capabilities.shared.isMobileDevice
            ? (evt) => {
                evt.preventDefault();
                evt.stopPropagation();
                evt.stopImmediatePropagation();
            }
            : $onEnd;
        if (dragPayload.initial) {
            call(controller.onBegin, dragPayload);
            call(controller.onPointerDown, dragPayload, dragPayload.down);
        }
        getLogger('HandleDrag', 'debug')('[Drag][onBegin]');
        // attach listeners
        const eventHandlers = {
            pointermove: $onMove,
            dragover: $onMove,
            pointerup: $onPointerUp,
            //            pointercancel: $onEnd, // NOTE: This event is dispatched randomly on Android (!) not randomly but due to touch event detection
            contextmenu: $onContext,
            visibilitychange: $onEnd,
        };
        // TODO: try adding classes to lock touch-events to node when user starts interaction
        getLogger('HandleDrag', 'debug')('[Drag][Register] ');
        Object.keys(eventHandlers).forEach(eventType => node.addEventListener(eventType, eventHandlers[eventType], true));
        const doubleTabDisposer = controller.disposeOnRelease === false
            ? addDoubleTapListener(node, evt => $onEnd(evt))
            : noop;
        const disposer = () => {
            getLogger('HandleDrag', 'debug')('[Drag][Unregister] ');
            Object.keys(eventHandlers).forEach(eventType => node.removeEventListener(eventType, eventHandlers[eventType], true));
            doubleTabDisposer();
        };
        pointerControllerRef.current = Object.assign(Object.assign({}, dragPayload), { disposer });
    }, [node, getDesignPoint]);
    // -----------------------
    // General mouse move (Hover, Selection, Insert)
    // -----------------------
    const onPointerMove = React.useCallback((evt) => {
        getLogger('HandleDrag', 'debug')('[Mouse][Move] useItemSelector', evt.button);
        const point = getDesignPoint(evt);
        // dont handle hover if controller is active
        if (pointerControllerRef.current) {
            return;
        }
        const state = getState();
        const hovered = getHoveredMarkerId(state);
        // is there anything under mouse
        if (point) {
            const markerId = getMarkerIdAtPoint(state, point);
            // if there is a marker our job is pretty much done
            if (markerId) {
                // it item under mouse is not in selection 'hover it'
                const selection = getSelection(state);
                if (markerId !== hovered && !selection.includes(markerId)) {
                    dispatch(ProjectActions.setHover({ id: markerId }));
                }
                return;
            }
        }
        // nothing under mouse so we can unmark the hovered item
        if (hovered) {
            dispatch(ProjectActions.clearHover());
        }
    }, [getDesignPoint, getState, dispatch]);
    const onPointerDown = React.useCallback((evt) => {
        var _a;
        getLogger('HandleDrag', 'debug')('[Mouse][Down] useItemHover');
        if (evt.target !== node) {
            return;
        }
        const point = getDesignPoint(evt);
        if (!point) {
            return;
        }
        const pointerController = pointerControllerRef.current;
        // if there is active drag just forward pointer down
        if (pointerController) {
            call((_a = pointerController.handler) === null || _a === void 0 ? void 0 : _a.onPointerDown, pointerController, point.toObject());
            return;
        }
        const state = getState();
        const hoveredMarkerId = getHoveredMarkerId(state);
        let pointedMarkerId;
        // check if we clicked a hovered item
        if (hoveredMarkerId) {
            const marker = getMarker(state, hoveredMarkerId);
            // to be sure run an extra hit-test
            if (hitTestMarker(marker, point)) {
                pointedMarkerId = marker.id;
            }
        }
        else {
            // find a clicked marker by position
            pointedMarkerId = getMarkerIdAtPoint(state, point);
        }
        const pointedMarker = pointedMarkerId
            ? getMarker(state, pointedMarkerId)
            : null;
        // this should never be a case
        if (pointedMarkerId && !pointedMarker) {
            getLogger('ItemSelector', 'warn')('Pointed marker does not exist in store');
            return;
        }
        // const activeTool = getActiveTool(state);
        // if there is an insert tool, try inserting a marker
        if (activeTool) {
            const canStartInsertingMarker = 
            // we can start if there is no marker under pointer
            !pointedMarker ||
                // or if a marker in pointer is in lower layer when switching to edit is not possible
                getMarkerLayer(pointedMarker.alias) <
                    getMarkerLayer(activeTool.alias);
            if (canStartInsertingMarker) {
                getLogger('HandleDrag', 'debug')('[Mouse] setInsertPayload');
                const controllerFactory = getMarkerInsertController(activeTool.alias, activeTool.defaults, activeTool.overrides);
                const controller = controllerFactory(dispatch, getState);
                if (!controller) {
                    console.warn(`Missing insert controller for tool ${activeTool}`);
                    return;
                }
                // start insert
                createController({
                    type: 'markerInsert',
                    itemId: null,
                    alias: activeTool.alias,
                    down: point.toObject(),
                    handler: controller,
                    initial: true,
                    disposer: noop,
                });
                // clear selection if any
                if (hasSelection(state)) {
                    dispatch(ProjectActions.clearSelection());
                }
                return;
            }
        }
        // start dragging clicked marker
        if (pointedMarker) {
            // only unlocked markers can be dragged around
            if (!pointedMarker.locked) {
                getLogger('HandleDrag', 'debug')('[Mouse] setDragPayload');
                const controller = getMarkerDragController([pointedMarker], dispatch, getState);
                if (!controller) {
                    console.warn(`Missing drag controller for tool ${activeTool}`);
                    return;
                }
                // start marker drag
                createController({
                    type: 'markerDrag',
                    itemId: pointedMarker.id,
                    initialPosition: {
                        x: pointedMarker.x,
                        y: pointedMarker.y,
                    },
                    down: point.toObject(),
                    handler: controller,
                    initial: true,
                    disposer: noop,
                });
            }
            // add marker to current selection
            if (!isMarkerSelected(state, pointedMarker.id)) {
                dispatch(ProjectActions.itemSelect({ id: pointedMarker.id }));
            }
            evt.stopImmediatePropagation();
        }
        // user clicked somewhere without selecting any items so clear selection
        else if (hasSelection(state)) {
            dispatch(ProjectActions.clearSelection());
            evt.stopImmediatePropagation();
        }
    }, [node, activeTool, createController, getDesignPoint, getState, dispatch]);
    // -----------------------
    // DOM level handlers
    // -----------------------
    // NOTE: extract to useHover
    React.useEffect(() => {
        if (!node) {
            return noop;
        }
        getLogger('HandleDrag', 'debug')('[Mouse][Register] move');
        node.addEventListener('pointermove', onPointerMove);
        return () => {
            getLogger('HandleDrag', 'debug')('[Mouse][Unregister] move');
            node.removeEventListener('pointermove', onPointerMove);
        };
    }, [node, onPointerMove]);
    React.useEffect(() => {
        if (!node) {
            return noop;
        }
        getLogger('HandleDrag', 'debug')('[Mouse][Register] down');
        node.addEventListener('pointerdown', onPointerDown, true);
        return () => {
            getLogger('HandleDrag', 'debug')('[Mouse][Unregister] down');
            node.removeEventListener('pointerdown', onPointerDown, true);
        };
    }, [node, onPointerDown]);
    // -----------------------
    // Remove drag handler on tool/node change
    // -----------------------
    React.useEffect(() => {
        getLogger('HandleDrag', 'debug')('[ToolChange] ');
        const pointerController = pointerControllerRef.current;
        if (pointerController) {
            const { onEnd } = pointerController.handler;
            call(onEnd, pointerController);
        }
        disposePointerController();
    }, [activeTool]);
    React.useEffect(() => () => disposePointerController(), [node]);
    return [setNode];
};
